// HKDSE 2021 Paper 1 Knowledge Graph Samples
// Organized by question types with systematic naming

export const hkdse2021Samples = {
  algebra: [
    {
      id: 'hkdse2021_algebra_01',
      problem: "Simplify $(\\alpha\\beta^3)(\\alpha^{-2}\\beta^4)^5$ and express the answer in positive indices.",
      problemType: "algebra_indices",
      difficulty: "intermediate",
      description: "Simplifying expressions with negative indices",
      graphData: {
        nodes: [
          {
            id: "negative_indices",
            name: "Negative Indices",
            type: "concepts",
            description: "Understanding negative indices and their properties",
            value: "a⁻ⁿ = 1/aⁿ",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "power_of_power",
            name: "Power of Power",
            type: "concepts",
            description: "When raising a power to another power, multiply the indices",
            value: "(aᵐ)ⁿ = aᵐⁿ",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "multiplication_of_powers",
            name: "Multiplication of Powers",
            type: "concepts",
            description: "When multiplying powers with the same base, add the indices",
            value: "aᵐ × aⁿ = aᵐ⁺ⁿ",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "step1_simplify_inner",
            name: "Step 1: Simplify Inner Bracket",
            type: "steps",
            description: "Apply power of power rule to (α⁻²β⁴)⁵",
            value: "(α⁻²β⁴)⁵ = α⁻¹⁰β²⁰",
            importance: 8,
            x: 0, y: 0, z: 25
          },
          {
            id: "step2_multiply",
            name: "Step 2: Multiply Terms",
            type: "steps",
            description: "Multiply αβ³ with α⁻¹⁰β²⁰",
            value: "αβ³ × α⁻¹⁰β²⁰ = α⁻⁹β²³",
            importance: 9,
            x: 0, y: 100, z: -15
          },
          {
            id: "step3_convert_negative",
            name: "Step 3: Convert to Positive Indices",
            type: "steps",
            description: "Convert negative indices to positive using reciprocal",
            value: "α⁻⁹β²³ = β²³/α⁹",
            importance: 10,
            x: 0, y: 200, z: 0
          },
          {
            id: "final_answer",
            name: "Final Answer",
            type: "values",
            description: "The simplified expression in positive indices",
            value: "β²³/α⁹",
            importance: 10,
            x: 0, y: 300, z: 0
          }
        ],
        links: [
          { from: "negative_indices", to: "step3_convert_negative", type: "enables", strength: 10 },
          { from: "power_of_power", to: "step1_simplify_inner", type: "enables", strength: 9 },
          { from: "multiplication_of_powers", to: "step2_multiply", type: "enables", strength: 9 },
          { from: "step1_simplify_inner", to: "step2_multiply", type: "leads to", strength: 10 },
          { from: "step2_multiply", to: "step3_convert_negative", type: "leads to", strength: 10 },
          { from: "step3_convert_negative", to: "final_answer", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'hkdse2021_algebra_02',
      problem: "Given that $a$ is the subject of the formula $\\frac{4-3a}{b} = 5$.",
      problemType: "algebra_formula",
      difficulty: "fundamental",
      description: "Rearranging formula to make a variable the subject",
      graphData: {
        nodes: [
          {
            id: "fraction_equation",
            name: "Fraction Equation",
            type: "concepts",
            description: "Equation with fraction involving variable a",
            value: "(4-3a)/b = 5",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "cross_multiplication",
            name: "Cross Multiplication",
            type: "concepts",
            description: "Method to eliminate fraction by multiplying both sides",
            value: "If a/b = c, then a = bc",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "step1_cross_multiply",
            name: "Step 1: Cross Multiply",
            type: "steps",
            description: "Multiply both sides by b to eliminate fraction",
            value: "4-3a = 5b",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "step2_subtract_constant",
            name: "Step 2: Subtract Constant",
            type: "steps",
            description: "Subtract 4 from both sides",
            value: "-3a = 5b - 4",
            importance: 8,
            x: 0, y: 0, z: 25
          },
          {
            id: "step3_divide_by_coefficient",
            name: "Step 3: Divide by Coefficient",
            type: "steps",
            description: "Divide both sides by -3",
            value: "a = (4-5b)/3",
            importance: 10,
            x: 0, y: 100, z: -15
          },
          {
            id: "final_answer",
            name: "Final Answer",
            type: "values",
            description: "a expressed in terms of b",
            value: "a = (4-5b)/3",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "fraction_equation", to: "cross_multiplication", type: "requires", strength: 9 },
          { from: "cross_multiplication", to: "step1_cross_multiply", type: "enables", strength: 10 },
          { from: "step1_cross_multiply", to: "step2_subtract_constant", type: "leads to", strength: 9 },
          { from: "step2_subtract_constant", to: "step3_divide_by_coefficient", type: "leads to", strength: 10 },
          { from: "step3_divide_by_coefficient", to: "final_answer", type: "equals", strength: 10 }
        ]
      }
    }
  ],
  
  factorization: [
    {
      id: 'hkdse2021_factorization_01',
      problem: "Factorize: (a) $6x^2 + xy - 2y^2$ (b) $8x - 4y - 6x^2 - xy + 2y^2$",
      problemType: "factorization",
      difficulty: "intermediate",
      description: "Factorization of quadratic expressions",
      graphData: {
        nodes: [
          {
            id: "quadratic_expression",
            name: "Quadratic Expression",
            type: "concepts",
            description: "Expression with x² and y² terms",
            value: "6x² + xy - 2y²",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "ac_method",
            name: "AC Method",
            type: "concepts",
            description: "Method for factoring ax² + bx + c",
            value: "Find factors of ac that add to b",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "step1_find_ac",
            name: "Step 1: Find AC",
            type: "steps",
            description: "Calculate a × c = 6 × (-2) = -12",
            value: "AC = 6 × (-2) = -12",
            importance: 8,
            x: 150, y: -100, z: 20
          },
          {
            id: "step2_find_factors",
            name: "Step 2: Find Factors",
            type: "steps",
            description: "Find factors of -12 that add to 1",
            value: "Factors: 4 and -3 (4 × -3 = -12, 4 + -3 = 1)",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "step3_split_middle",
            name: "Step 3: Split Middle Term",
            type: "steps",
            description: "Rewrite as 6x² + 4xy - 3xy - 2y²",
            value: "6x² + 4xy - 3xy - 2y²",
            importance: 8,
            x: 0, y: 100, z: -15
          },
          {
            id: "step4_group_factor",
            name: "Step 4: Group and Factor",
            type: "steps",
            description: "Factor by grouping",
            value: "(6x² + 4xy) + (-3xy - 2y²) = 2x(3x + 2y) - y(3x + 2y)",
            importance: 9,
            x: 0, y: 200, z: 0
          },
          {
            id: "final_answer_a",
            name: "Answer (a)",
            type: "values",
            description: "Final factored form",
            value: "(3x + 2y)(2x - y)",
            importance: 10,
            x: 0, y: 300, z: 0
          },
          {
            id: "part_b_rearrange",
            name: "Part (b): Rearrange",
            type: "steps",
            description: "Rearrange terms to match part (a)",
            value: "8x - 4y - 6x² - xy + 2y² = -6x² - xy + 2y² + 8x - 4y",
            importance: 8,
            x: -200, y: 150, z: 0
          },
          {
            id: "part_b_factor",
            name: "Part (b): Factor",
            type: "steps",
            description: "Factor out common terms",
            value: "-(6x² + xy - 2y²) + 4(2x - y)",
            importance: 9,
            x: 200, y: 150, z: 0
          },
          {
            id: "final_answer_b",
            name: "Answer (b)",
            type: "values",
            description: "Final factored form for part (b)",
            value: "-(3x + 2y)(2x - y) + 4(2x - y) = (2x - y)(-3x - 2y + 4)",
            importance: 10,
            x: 0, y: 400, z: 0
          }
        ],
        links: [
          { from: "quadratic_expression", to: "ac_method", type: "requires", strength: 9 },
          { from: "ac_method", to: "step1_find_ac", type: "enables", strength: 8 },
          { from: "step1_find_ac", to: "step2_find_factors", type: "leads to", strength: 9 },
          { from: "step2_find_factors", to: "step3_split_middle", type: "enables", strength: 8 },
          { from: "step3_split_middle", to: "step4_group_factor", type: "leads to", strength: 9 },
          { from: "step4_group_factor", to: "final_answer_a", type: "equals", strength: 10 },
          { from: "final_answer_a", to: "part_b_rearrange", type: "enables", strength: 8 },
          { from: "part_b_rearrange", to: "part_b_factor", type: "leads to", strength: 9 },
          { from: "part_b_factor", to: "final_answer_b", type: "equals", strength: 10 }
        ]
      }
    }
  ],
  
  inequalities: [
    {
      id: 'hkdse2021_inequalities_01',
      problem: "Find the range of values of $x$ that satisfy both $\\frac{7(x-2)}{5} + 11 > 3(x-1)$ and $x + 4 \\geq 0$.",
      problemType: "inequalities_compound",
      difficulty: "intermediate",
      description: "Solving compound inequalities",
      graphData: {
        nodes: [
          {
            id: "compound_inequalities",
            name: "Compound Inequalities",
            type: "concepts",
            description: "Two inequalities that must both be satisfied",
            value: "Inequality 1 AND Inequality 2",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "inequality1",
            name: "Inequality 1",
            type: "concepts",
            description: "First inequality to solve",
            value: "7(x-2)/5 + 11 > 3(x-1)",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "inequality2",
            name: "Inequality 2",
            type: "concepts",
            description: "Second inequality to solve",
            value: "x + 4 ≥ 0",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "step1_solve_ineq1",
            name: "Step 1: Solve Inequality 1",
            type: "steps",
            description: "Solve 7(x-2)/5 + 11 > 3(x-1)",
            value: "7(x-2)/5 + 11 > 3(x-1)",
            importance: 9,
            x: -200, y: 0, z: 25
          },
          {
            id: "step1a_clear_fraction",
            name: "Step 1a: Clear Fraction",
            type: "steps",
            description: "Multiply both sides by 5",
            value: "7(x-2) + 55 > 15(x-1)",
            importance: 8,
            x: -200, y: 50, z: 0
          },
          {
            id: "step1b_expand",
            name: "Step 1b: Expand",
            type: "steps",
            description: "Expand brackets",
            value: "7x - 14 + 55 > 15x - 15",
            importance: 8,
            x: -200, y: 100, z: 0
          },
          {
            id: "step1c_collect_terms",
            name: "Step 1c: Collect Terms",
            type: "steps",
            description: "Collect like terms",
            value: "7x + 41 > 15x - 15",
            importance: 8,
            x: -200, y: 150, z: 0
          },
          {
            id: "step1d_solve",
            name: "Step 1d: Solve for x",
            type: "steps",
            description: "Solve the inequality",
            value: "56 > 8x → x < 7",
            importance: 9,
            x: -200, y: 200, z: 0
          },
          {
            id: "step2_solve_ineq2",
            name: "Step 2: Solve Inequality 2",
            type: "steps",
            description: "Solve x + 4 ≥ 0",
            value: "x ≥ -4",
            importance: 8,
            x: 200, y: 0, z: 25
          },
          {
            id: "step3_combine",
            name: "Step 3: Combine Solutions",
            type: "steps",
            description: "Find intersection of both solutions",
            value: "x < 7 AND x ≥ -4",
            importance: 9,
            x: 0, y: 100, z: -15
          },
          {
            id: "final_answer",
            name: "Final Answer",
            type: "values",
            description: "Range of values satisfying both inequalities",
            value: "-4 ≤ x < 7",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "compound_inequalities", to: "inequality1", type: "includes", strength: 9 },
          { from: "compound_inequalities", to: "inequality2", type: "includes", strength: 9 },
          { from: "inequality1", to: "step1_solve_ineq1", type: "requires", strength: 9 },
          { from: "step1_solve_ineq1", to: "step1a_clear_fraction", type: "leads to", strength: 8 },
          { from: "step1a_clear_fraction", to: "step1b_expand", type: "leads to", strength: 8 },
          { from: "step1b_expand", to: "step1c_collect_terms", type: "leads to", strength: 8 },
          { from: "step1c_collect_terms", to: "step1d_solve", type: "leads to", strength: 9 },
          { from: "inequality2", to: "step2_solve_ineq2", type: "requires", strength: 8 },
          { from: "step1d_solve", to: "step3_combine", type: "enables", strength: 9 },
          { from: "step2_solve_ineq2", to: "step3_combine", type: "enables", strength: 9 },
          { from: "step3_combine", to: "final_answer", type: "equals", strength: 10 }
        ]
      }
    }
  ],
  
  word_problems: [
    {
      id: 'hkdse2021_word_problem_01',
      problem: "A boy has 3 times as many stickers as a girl. If the boy gives 20 stickers to the girl, then the girl will have twice as many stickers as the boy. Find the total number of stickers that the boy and the girl have.",
      problemType: "word_problem_linear",
      difficulty: "intermediate",
      description: "Linear word problem with variables",
      graphData: {
        nodes: [
          {
            id: "initial_condition",
            name: "Initial Condition",
            type: "concepts",
            description: "Boy has 3 times as many stickers as girl",
            value: "Let girl have x stickers, boy has 3x stickers",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "after_transfer",
            name: "After Transfer",
            type: "concepts",
            description: "After boy gives 20 stickers to girl",
            value: "Girl: x + 20, Boy: 3x - 20",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "final_condition",
            name: "Final Condition",
            type: "concepts",
            description: "Girl has twice as many as boy",
            value: "x + 20 = 2(3x - 20)",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "step1_setup_equation",
            name: "Step 1: Setup Equation",
            type: "steps",
            description: "Set up the equation based on final condition",
            value: "x + 20 = 2(3x - 20)",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "step2_expand",
            name: "Step 2: Expand",
            type: "steps",
            description: "Expand the right side",
            value: "x + 20 = 6x - 40",
            importance: 8,
            x: 0, y: 50, z: 0
          },
          {
            id: "step3_collect_terms",
            name: "Step 3: Collect Terms",
            type: "steps",
            description: "Collect like terms",
            value: "60 = 5x",
            importance: 8,
            x: 0, y: 100, z: 0
          },
          {
            id: "step4_solve",
            name: "Step 4: Solve for x",
            type: "steps",
            description: "Solve for x",
            value: "x = 12",
            importance: 9,
            x: 0, y: 150, z: 0
          },
          {
            id: "step5_find_total",
            name: "Step 5: Find Total",
            type: "steps",
            description: "Calculate total stickers",
            value: "Total = x + 3x = 4x = 48",
            importance: 10,
            x: 0, y: 200, z: 0
          },
          {
            id: "final_answer",
            name: "Final Answer",
            type: "values",
            description: "Total number of stickers",
            value: "48 stickers",
            importance: 10,
            x: 0, y: 250, z: 0
          }
        ],
        links: [
          { from: "initial_condition", to: "after_transfer", type: "enables", strength: 9 },
          { from: "after_transfer", to: "final_condition", type: "enables", strength: 9 },
          { from: "final_condition", to: "step1_setup_equation", type: "enables", strength: 10 },
          { from: "step1_setup_equation", to: "step2_expand", type: "leads to", strength: 8 },
          { from: "step2_expand", to: "step3_collect_terms", type: "leads to", strength: 8 },
          { from: "step3_collect_terms", to: "step4_solve", type: "leads to", strength: 9 },
          { from: "step4_solve", to: "step5_find_total", type: "enables", strength: 10 },
          { from: "step5_find_total", to: "final_answer", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'hkdse2021_word_problem_02',
      problem: "The marked price of a shirt is $80$ more than its cost. The shirt is sold at a 10% discount from its marked price. After selling the shirt, the profit percentage is 30%. Find the marked price of the shirt.",
      problemType: "word_problem_percentage_profit",
      difficulty: "intermediate",
      description: "Solving percentage and profit problems",
      graphData: {
        nodes: [
          {
            id: "cost_price",
            name: "Cost Price",
            type: "concepts",
            description: "Let cost price be x",
            value: "CP = x",
            importance: 8,
            x: -200, y: -200, z: 0
          },
          {
            id: "marked_price",
            name: "Marked Price",
            type: "concepts",
            description: "Marked price is $80 more than cost",
            value: "MP = x + 80",
            importance: 9,
            x: 0, y: -200, z: 0
          },
          {
            id: "selling_price",
            name: "Selling Price",
            type: "concepts",
            description: "Sold at 10% discount from marked price",
            value: "SP = 0.9 × MP",
            importance: 9,
            x: 200, y: -200, z: 0
          },
          {
            id: "profit_percentage",
            name: "Profit Percentage",
            type: "concepts",
            description: "Profit percentage is 30%",
            value: "SP = 1.3 × CP",
            importance: 10,
            x: 0, y: 0, z: 0
          },
          {
            id: "step1_express_sp",
            name: "Step 1: Express SP in terms of x",
            type: "steps",
            description: "SP = 0.9(x + 80)",
            value: "SP = 0.9x + 72",
            importance: 9,
            x: 0, y: 100, z: 0
          },
          {
            id: "step2_set_equation",
            name: "Step 2: Set up equation",
            type: "steps",
            description: "0.9x + 72 = 1.3x",
            value: "0.9x + 72 = 1.3x",
            importance: 10,
            x: 0, y: 200, z: 0
          },
          {
            id: "step3_solve",
            name: "Step 3: Solve for x",
            type: "steps",
            description: "Solve the linear equation",
            value: "x = 180",
            importance: 9,
            x: 0, y: 300, z: 0
          },
          {
            id: "final_answer",
            name: "Final Answer",
            type: "values",
            description: "Marked price of the shirt",
            value: "MP = 180 + 80 = $260",
            importance: 10,
            x: 0, y: 400, z: 0
          }
        ],
        links: [
          { from: "cost_price", to: "marked_price", type: "enables", strength: 9 },
          { from: "marked_price", to: "selling_price", type: "enables", strength: 9 },
          { from: "profit_percentage", to: "step2_set_equation", type: "enables", strength: 10 },
          { from: "selling_price", to: "step1_express_sp", type: "leads to", strength: 10 },
          { from: "step1_express_sp", to: "step2_set_equation", type: "leads to", strength: 10 },
          { from: "step2_set_equation", to: "step3_solve", type: "leads to", strength: 10 },
          { from: "step3_solve", to: "final_answer", type: "leads to", strength: 10 }
        ]
      }
    }
  ],
  trigonometry: [
    {
      id: 'hkdse2021_trigonometry_01',
      problem: "In a polar coordinate system, $O$ is the pole. The polar coordinates of points $P$ and $Q$ are $(r, 80°)$ and $(r, 140°)$ respectively, where $r$ is a positive constant. Given that the distance between $P$ and $Q$ is 21, find: (a) $\\angle POQ$ (b) $r$ (c) The perimeter of $\\triangle OPQ$",
      problemType: "trigonometry_polar_coordinates",
      difficulty: "harder",
      description: "Solving trigonometry problems using polar coordinates",
      graphData: {
        nodes: [
          {
            id: "polar_coordinates",
            name: "Polar Coordinates",
            type: "concepts",
            description: "Understanding polar coordinate system",
            value: "P(r, 80°), Q(r, 140°)",
            importance: 9,
            x: -200, y: -200, z: 0
          },
          {
            id: "angle_poq",
            name: "Angle POQ",
            type: "concepts",
            description: "Angle between OP and OQ",
            value: "∠POQ = 140° - 80° = 60°",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "cosine_law",
            name: "Cosine Law",
            type: "formulas",
            description: "Using cosine law to find distance",
            value: "PQ² = OP² + OQ² - 2×OP×OQ×cos(∠POQ)",
            importance: 10,
            x: 200, y: -200, z: 0
          },
          {
            id: "step1_apply_cosine_law",
            name: "Step 1: Apply Cosine Law",
            type: "steps",
            description: "21² = r² + r² - 2×r×r×cos(60°)",
            value: "441 = 2r² - 2r²×0.5",
            importance: 9,
            x: 0, y: 0, z: 0
          },
          {
            id: "step2_simplify",
            name: "Step 2: Simplify",
            type: "steps",
            description: "441 = 2r² - r² = r²",
            value: "r² = 441",
            importance: 8,
            x: 0, y: 100, z: 0
          },
          {
            id: "step3_find_r",
            name: "Step 3: Find r",
            type: "steps",
            description: "r = √441 = 21",
            value: "r = 21",
            importance: 9,
            x: 0, y: 200, z: 0
          },
          {
            id: "step4_perimeter",
            name: "Step 4: Find Perimeter",
            type: "steps",
            description: "Perimeter = OP + OQ + PQ",
            value: "Perimeter = 21 + 21 + 21 = 63",
            importance: 8,
            x: 0, y: 300, z: 0
          },
          {
            id: "final_answer",
            name: "Final Answer",
            type: "values",
            description: "Complete solution",
            value: "(a) 60° (b) 21 (c) 63",
            importance: 10,
            x: 0, y: 400, z: 0
          }
        ],
        links: [
          { from: "polar_coordinates", to: "angle_poq", type: "enables", strength: 10 },
          { from: "angle_poq", to: "step1_apply_cosine_law", type: "enables", strength: 10 },
          { from: "cosine_law", to: "step1_apply_cosine_law", type: "enables", strength: 10 },
          { from: "step1_apply_cosine_law", to: "step2_simplify", type: "leads to", strength: 10 },
          { from: "step2_simplify", to: "step3_find_r", type: "leads to", strength: 10 },
          { from: "step3_find_r", to: "step4_perimeter", type: "leads to", strength: 9 },
          { from: "step4_perimeter", to: "final_answer", type: "leads to", strength: 10 }
        ]
      }
    },
    {
      id: 'hkdse2021_mensuration_01',
      problem: "The base radii of solid right circular cylinders $X$ and solid right circular cone $Y$ are 20 cm and 24 cm respectively. The volume of solid right circular cone $Z$ is equal to the sum of the volumes of $X$ and $Y$. The base radius of $Z$ is equal to the base diameter of $X$. A craftsman knows that the volume of $Y$ is $800\\pi$ cm³. (a) Find the base radius of $Y$. (b) Are $Y$ and $Z$ similar? Explain your answer. (c) The craftsman claims that the sum of the curved surface areas of $X$ and $Y$ is greater than the curved surface area of $Z$. Do you agree? Explain your answer.",
      problemType: "mensuration_3d_solids",
      difficulty: "harder",
      description: "Advanced 3D mensuration with cones and cylinders",
      graphData: {
        nodes: [
          {
            id: "volume_formulas",
            name: "Volume Formulas",
            type: "concepts",
            description: "Cylinder: πr²h, Cone: ⅓πr²h",
            value: "V_cylinder = πr²h, V_cone = ⅓πr²h",
            importance: 9,
            x: -200, y: -200, z: 0
          },
          {
            id: "given_volumes",
            name: "Given Volumes",
            type: "concepts",
            description: "V_Y = 800π, V_Z = V_X + V_Y",
            value: "V_Y = 800π cm³",
            importance: 8,
            x: 0, y: -200, z: 0
          },
          {
            id: "part_a_find_radius_y",
            name: "Part (a): Find Radius Y",
            type: "steps",
            description: "Use volume formula for cone Y",
            value: "800π = ⅓π(24)²h_Y → h_Y = 25/6",
            importance: 8,
            x: 200, y: -200, z: 0
          },
          {
            id: "part_b_similarity_check",
            name: "Part (b): Similarity Check",
            type: "steps",
            description: "Check if Y and Z are similar",
            value: "Compare corresponding ratios",
            importance: 8,
            x: 0, y: 0, z: 0
          },
          {
            id: "part_c_surface_area",
            name: "Part (c): Surface Area",
            type: "steps",
            description: "Compare curved surface areas",
            value: "Calculate and compare areas",
            importance: 8,
            x: 0, y: 100, z: 0
          },
          {
            id: "final_answers",
            name: "Final Answers",
            type: "values",
            description: "All required answers",
            value: "(a) 24 cm (b) No (c) Yes, agree",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "volume_formulas", to: "part_a_find_radius_y", type: "enables", strength: 9 },
          { from: "given_volumes", to: "part_a_find_radius_y", type: "enables", strength: 8 },
          { from: "part_a_find_radius_y", to: "part_b_similarity_check", type: "enables", strength: 8 },
          { from: "part_b_similarity_check", to: "part_c_surface_area", type: "enables", strength: 8 },
          { from: "part_a_find_radius_y", to: "final_answers", type: "contributes to", strength: 8 },
          { from: "part_b_similarity_check", to: "final_answers", type: "contributes to", strength: 8 },
          { from: "part_c_surface_area", to: "final_answers", type: "contributes to", strength: 8 }
        ]
      }
    },
    {
      id: 'hkdse2021_probability_01',
      problem: "7 teachers and 3 students are randomly arranged in a row. (a) How many different arrangements are possible? (b) Find the probability that no students are adjacent to each other in the arrangement.",
      problemType: "probability_permutation",
      difficulty: "intermediate",
      description: "Probability with permutation and arrangement",
      graphData: {
        nodes: [
          {
            id: "total_arrangements",
            name: "Total Arrangements",
            type: "concepts",
            description: "Total number of people",
            value: "10 people total",
            importance: 8,
            x: -200, y: -200, z: 0
          },
          {
            id: "part_a_total_permutations",
            name: "Part (a): Total Permutations",
            type: "steps",
            description: "Calculate total arrangements",
            value: "10! = 3,628,800",
            importance: 8,
            x: 0, y: -200, z: 0
          },
          {
            id: "part_b_favorable",
            name: "Part (b): Favorable Cases",
            type: "steps",
            description: "No students adjacent",
            value: "Use gap method",
            importance: 9,
            x: 200, y: -200, z: 0
          },
          {
            id: "part_b_gap_method",
            name: "Part (b): Gap Method",
            type: "steps",
            description: "Arrange teachers first, then students",
            value: "7! × C(8,3) × 3!",
            importance: 9,
            x: 0, y: 0, z: 0
          },
          {
            id: "part_b_calculation",
            name: "Part (b): Calculation",
            type: "steps",
            description: "Calculate probability",
            value: "5040 × 56 × 6 / 3628800 = 7/15",
            importance: 8,
            x: 0, y: 100, z: 0
          },
          {
            id: "final_answers",
            name: "Final Answers",
            type: "values",
            description: "All required answers",
            value: "(a) 3,628,800 (b) 7/15",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "total_arrangements", to: "part_a_total_permutations", type: "enables", strength: 8 },
          { from: "part_a_total_permutations", to: "part_b_favorable", type: "enables", strength: 8 },
          { from: "part_b_favorable", to: "part_b_gap_method", type: "leads to", strength: 9 },
          { from: "part_b_gap_method", to: "part_b_calculation", type: "leads to", strength: 9 },
          { from: "part_a_total_permutations", to: "final_answers", type: "contributes to", strength: 8 },
          { from: "part_b_calculation", to: "final_answers", type: "contributes to", strength: 8 }
        ]
      }
    },
    {
      id: 'hkdse2021_linear_programming_01',
      problem: "Lines $L_1$ and $L_2$ are perpendicular to each other. The $y$-intercept of $L_1$ is 3. It is known that $L_1$ and $L_2$ intersect at point $(2,6)$. Let $R$ be the region enclosed by $L_1$, $L_2$ and the $x$-axis (including the boundary). (a) Given that $R$ represents the solution set of a system of inequalities, find this system of inequalities. (b) Find the minimum value of $8x-5y$, where $(x,y)$ is a point in $R$.",
      problemType: "linear_programming_optimization",
      difficulty: "harder",
      description: "Linear programming with optimization",
      graphData: {
        nodes: [
          {
            id: "line_equations",
            name: "Line Equations",
            type: "concepts",
            description: "Find equations of L₁ and L₂",
            value: "L₁: y = mx + 3, L₂: y = -x/m + b",
            importance: 9,
            x: -200, y: -200, z: 0
          },
          {
            id: "intersection_point",
            name: "Intersection Point",
            type: "concepts",
            description: "Lines meet at (2,6)",
            value: "Substitute (2,6) into both equations",
            importance: 9,
            x: 0, y: -200, z: 0
          },
          {
            id: "step1_find_slopes",
            name: "Step 1: Find Slopes",
            type: "steps",
            description: "Solve for m and b",
            value: "m = 3/2, b = 9",
            importance: 8,
            x: 200, y: -200, z: 0
          },
          {
            id: "step2_inequalities",
            name: "Step 2: System of Inequalities",
            type: "steps",
            description: "Form inequalities for region R",
            value: "y ≥ 0, y ≤ 3x/2 + 3, y ≤ -2x/3 + 9",
            importance: 9,
            x: 0, y: 0, z: 0
          },
          {
            id: "step3_optimization",
            name: "Step 3: Optimization",
            type: "steps",
            description: "Find minimum of 8x-5y",
            value: "Use corner point method",
            importance: 8,
            x: 0, y: 100, z: 0
          },
          {
            id: "final_answers",
            name: "Final Answers",
            type: "values",
            description: "All required answers",
            value: "(a) y ≥ 0, y ≤ 3x/2 + 3, y ≤ -2x/3 + 9 (b) -15",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "line_equations", to: "intersection_point", type: "enables", strength: 9 },
          { from: "intersection_point", to: "step1_find_slopes", type: "leads to", strength: 9 },
          { from: "step1_find_slopes", to: "step2_inequalities", type: "enables", strength: 9 },
          { from: "step2_inequalities", to: "step3_optimization", type: "enables", strength: 8 },
          { from: "step2_inequalities", to: "final_answers", type: "contributes to", strength: 9 },
          { from: "step3_optimization", to: "final_answers", type: "contributes to", strength: 8 }
        ]
      }
    },
    {
      id: 'hkdse2021_sequences_01',
      problem: "Let $A(n)$ be the $n$th term of an arithmetic sequence. Given that $A(5)=26$ and $A(12)=61$. (a) Find $A(1)$. (b) Suppose for any positive integer $n$, $\\log_2 G(n) = A(n)$. Find the maximum value of $k$ such that $\\log_8(G(1)G(2)G(3)\\cdots G(k)) < 999$.",
      problemType: "arithmetic_sequences_logarithms",
      difficulty: "harder",
      description: "Arithmetic sequences with logarithmic functions",
      graphData: {
        nodes: [
          {
            id: "arithmetic_sequence",
            name: "Arithmetic Sequence",
            type: "concepts",
            description: "A(n) = A(1) + (n-1)d",
            value: "A(n) = A(1) + (n-1)d",
            importance: 9,
            x: -200, y: -200, z: 0
          },
          {
            id: "given_terms",
            name: "Given Terms",
            type: "concepts",
            description: "A(5) = 26, A(12) = 61",
            value: "A(5) = 26, A(12) = 61",
            importance: 8,
            x: 0, y: -200, z: 0
          },
          {
            id: "step1_find_d",
            name: "Step 1: Find Common Difference",
            type: "steps",
            description: "Use A(12) - A(5) = 7d",
            value: "61 - 26 = 35 = 7d → d = 5",
            importance: 8,
            x: 200, y: -200, z: 0
          },
          {
            id: "step2_find_a1",
            name: "Step 2: Find A(1)",
            type: "steps",
            description: "Use A(5) = A(1) + 4d",
            value: "26 = A(1) + 20 → A(1) = 6",
            importance: 8,
            x: 0, y: 0, z: 0
          },
          {
            id: "part_b_logarithmic_function",
            name: "Part (b): Logarithmic Function",
            type: "steps",
            description: "G(n) = 2^A(n)",
            value: "G(n) = 2^(6 + 5(n-1))",
            importance: 9,
            x: 0, y: 100, z: 0
          },
          {
            id: "part_b_product_log",
            name: "Part (b): Product Logarithm",
            type: "steps",
            description: "Find log₈ of product",
            value: "log₈(G(1)G(2)...G(k)) = log₈(2^(sum of A(n)))",
            importance: 8,
            x: 0, y: 200, z: 0
          },
          {
            id: "part_b_solve_k",
            name: "Part (b): Solve for k",
            type: "steps",
            description: "Find maximum k",
            value: "k = 15",
            importance: 8,
            x: 0, y: 300, z: 0
          },
          {
            id: "final_answers",
            name: "Final Answers",
            type: "values",
            description: "All required answers",
            value: "(a) 6 (b) 15",
            importance: 10,
            x: 0, y: 400, z: 0
          }
        ],
        links: [
          { from: "arithmetic_sequence", to: "step1_find_d", type: "enables", strength: 8 },
          { from: "given_terms", to: "step1_find_d", type: "enables", strength: 8 },
          { from: "step1_find_d", to: "step2_find_a1", type: "enables", strength: 8 },
          { from: "step2_find_a1", to: "part_b_logarithmic_function", type: "enables", strength: 9 },
          { from: "part_b_logarithmic_function", to: "part_b_product_log", type: "leads to", strength: 8 },
          { from: "part_b_product_log", to: "part_b_solve_k", type: "leads to", strength: 8 },
          { from: "step2_find_a1", to: "final_answers", type: "contributes to", strength: 8 },
          { from: "part_b_solve_k", to: "final_answers", type: "contributes to", strength: 8 }
        ]
      }
    },
    {
      id: 'hkdse2021_3d_geometry_01',
      problem: "(a) Metal sheet $ABCD$ has the shape of a ladder, where $AD \\parallel BC$. Given that $AB = 45$ cm, $\\angle ADC = 70°$ and $\\angle BAD = 50°$, find $CD$. (b) Now consider the metal sheet $ABCD$ described in part (a). Let $E$ be a point on $AD$ such that $BE$ is perpendicular to $AD$. The metal sheet is folded along $BE$ so that $AE$ is perpendicular to plane $BCDE$. The triangular metal sheet is placed on the already folded metal sheet to form a triangular pyramid (see Figure 2). Given that $BC = 40$ cm. (i) Find $\\angle CAD$. (ii) Is the angle between plane $ACD$ and plane $BCDE$ greater than $30°$? Explain your answer.",
      problemType: "3d_geometry_trigonometry",
      difficulty: "harder",
      description: "3D geometry with trigonometry and folding",
      graphData: {
        nodes: [
          {
            id: "part_a_trapezoid",
            name: "Part (a): Trapezoid",
            type: "concepts",
            description: "ABCD is a trapezoid with AD || BC",
            value: "Use trigonometry to find CD",
            importance: 8,
            x: -200, y: -200, z: 0
          },
          {
            id: "part_a_trigonometry",
            name: "Part (a): Trigonometry",
            type: "steps",
            description: "Use sine rule or cosine rule",
            value: "CD = 45 × sin(50°)/sin(70°)",
            importance: 8,
            x: 0, y: -200, z: 0
          },
          {
            id: "part_a_calculation",
            name: "Part (a): Calculation",
            type: "steps",
            description: "Calculate CD",
            value: "CD ≈ 37.5 cm",
            importance: 8,
            x: 200, y: -200, z: 0
          },
          {
            id: "part_b_3d_structure",
            name: "Part (b): 3D Structure",
            type: "concepts",
            description: "Triangular pyramid formed by folding",
            value: "Use 3D geometry concepts",
            importance: 9,
            x: 0, y: 0, z: 0
          },
          {
            id: "part_b_angle_cad",
            name: "Part (b): Angle CAD",
            type: "steps",
            description: "Find angle CAD",
            value: "Use cosine rule in triangle ACD",
            importance: 8,
            x: 0, y: 100, z: 0
          },
          {
            id: "part_b_plane_angle",
            name: "Part (b): Plane Angle",
            type: "steps",
            description: "Find angle between planes",
            value: "Compare with 30°",
            importance: 8,
            x: 0, y: 200, z: 0
          },
          {
            id: "final_answers",
            name: "Final Answers",
            type: "values",
            description: "All required answers",
            value: "(a) 37.5 cm (b) (i) 60° (ii) Yes",
            importance: 10,
            x: 0, y: 300, z: 0
          }
        ],
        links: [
          { from: "part_a_trapezoid", to: "part_a_trigonometry", type: "enables", strength: 8 },
          { from: "part_a_trigonometry", to: "part_a_calculation", type: "leads to", strength: 8 },
          { from: "part_a_calculation", to: "part_b_3d_structure", type: "enables", strength: 9 },
          { from: "part_b_3d_structure", to: "part_b_angle_cad", type: "enables", strength: 8 },
          { from: "part_b_angle_cad", to: "part_b_plane_angle", type: "enables", strength: 8 },
          { from: "part_a_calculation", to: "final_answers", type: "contributes to", strength: 8 },
          { from: "part_b_angle_cad", to: "final_answers", type: "contributes to", strength: 8 },
          { from: "part_b_plane_angle", to: "final_answers", type: "contributes to", strength: 8 }
        ]
      }
    },
    {
      id: 'hkdse2021_quadratic_advanced_01',
      problem: "Let $f(x) = x^2 - 12kx - 14x + 36k^2 + 89k + 53$, where $k$ is a positive constant. In the same rectangular coordinate system, let the vertices of the graphs of $y = f(x)$ and $y = f(14-x)$ be denoted as $Q$ and $R$ respectively. (a) Using the method of completing the square, express the coordinates of $Q$ in terms of $k$. (b) Find the coordinates of $R$, expressing your answer in terms of $k$. (c) The coordinates of point $S$ are $(7, 4-3k)$. Let the incircle of $\\triangle QRS$ be denoted as $C$. (i) Express the equation of the line passing through $Q$ and $S$ in terms of $k$. (ii) Express the equation of $C$ in terms of $k$. (iii) Suppose $QS$ is a tangent to $C$ at point $T$. Let $U$ be the center of $C$. Given that the coordinates of point $V$ are $(-29, -14)$, is it possible for $STUV$ to be a rectangle? Explain your answer.",
      problemType: "quadratic_advanced_geometry",
      difficulty: "harder",
      description: "Advanced quadratic functions with geometry",
      graphData: {
        nodes: [
          {
            id: "quadratic_function",
            name: "Quadratic Function",
            type: "concepts",
            description: "f(x) = x² - 12kx - 14x + 36k² + 89k + 53",
            value: "Complete the square to find vertex",
            importance: 9,
            x: -200, y: -200, z: 0
          },
          {
            id: "part_a_completing_square",
            name: "Part (a): Completing Square",
            type: "steps",
            description: "Rewrite in vertex form",
            value: "f(x) = (x - (6k+7))² + (36k² + 89k + 53 - (6k+7)²)",
            importance: 9,
            x: 0, y: -200, z: 0
          },
          {
            id: "part_a_vertex_q",
            name: "Part (a): Vertex Q",
            type: "steps",
            description: "Coordinates of Q",
            value: "Q(6k+7, 36k² + 89k + 53 - (6k+7)²)",
            importance: 8,
            x: 200, y: -200, z: 0
          },
          {
            id: "part_b_vertex_r",
            name: "Part (b): Vertex R",
            type: "steps",
            description: "Find vertex of f(14-x)",
            value: "R(7-6k, same y-coordinate as Q)",
            importance: 8,
            x: 0, y: 0, z: 0
          },
          {
            id: "part_c_triangle_qrs",
            name: "Part (c): Triangle QRS",
            type: "concepts",
            description: "Triangle with vertices Q, R, S",
            value: "Find incircle of triangle",
            importance: 9,
            x: 0, y: 100, z: 0
          },
          {
            id: "part_c_line_qs",
            name: "Part (c): Line QS",
            type: "steps",
            description: "Equation of line through Q and S",
            value: "Use point-slope form",
            importance: 8,
            x: 0, y: 200, z: 0
          },
          {
            id: "part_c_incircle",
            name: "Part (c): Incircle C",
            type: "steps",
            description: "Equation of incircle",
            value: "Use incircle formula",
            importance: 8,
            x: 0, y: 300, z: 0
          },
          {
            id: "part_c_rectangle_check",
            name: "Part (c): Rectangle Check",
            type: "steps",
            description: "Check if STUV is rectangle",
            value: "Verify properties of rectangle",
            importance: 8,
            x: 0, y: 400, z: 0
          },
          {
            id: "final_answers",
            name: "Final Answers",
            type: "values",
            description: "All required answers",
            value: "Complex expressions in terms of k",
            importance: 10,
            x: 0, y: 500, z: 0
          }
        ],
        links: [
          { from: "quadratic_function", to: "part_a_completing_square", type: "leads to", strength: 9 },
          { from: "part_a_completing_square", to: "part_a_vertex_q", type: "leads to", strength: 9 },
          { from: "part_a_vertex_q", to: "part_b_vertex_r", type: "enables", strength: 8 },
          { from: "part_b_vertex_r", to: "part_c_triangle_qrs", type: "enables", strength: 9 },
          { from: "part_c_triangle_qrs", to: "part_c_line_qs", type: "enables", strength: 8 },
          { from: "part_c_line_qs", to: "part_c_incircle", type: "enables", strength: 8 },
          { from: "part_c_incircle", to: "part_c_rectangle_check", type: "enables", strength: 8 },
          { from: "part_a_vertex_q", to: "final_answers", type: "contributes to", strength: 8 },
          { from: "part_b_vertex_r", to: "final_answers", type: "contributes to", strength: 8 },
          { from: "part_c_rectangle_check", to: "final_answers", type: "contributes to", strength: 8 }
        ]
      }
    }
  ]
}; 